import type { Config } from "../../../../aysa_api/models/config.types";
import type { TargetGroup } from "../../../../aysa_api/models/target_group.types";
import type {
  AppliedFilters,
  FilterSection,
} from "../../../../aysa_api/models/filter_section.types";
import type { ReductionsState } from "../../../../aysa_api/models/reductions_state.types";
import type { SortOptions } from "../../../../aysa_api/models/sort_options.types";
import type { Category } from "../../../../aysa_api/models/category.types";
import type {
  NavigationSection_Category,
  NavigationSection,
} from "../../../../aysa_api/models/navigation_section.types";
import type { Image } from "../../../../aysa_api/models/image.types";
import type {
  PromotionTracker,
  SectionTracker,
} from "../../../../aysa_api/models/tracker.types";
import type {
  AnySession,
  Session,
} from "../../../../aysa_api/models/session.types";
import type { ProductImageTypes } from "../../../../aysa_api/models/product_images.types";
import type { LinkTarget } from "../../../../aysa_api/models/link_target.types";
import type { Video } from "../../../../aysa_api/models/video.types";
import type { AspectRatio } from "../../../../aysa_api/models/aspect_ratio.types";
import type { ProductTileV2 } from "../../../../aysa_api/models/product_tile_v2.types";
import type { TreeNavigationItem } from "../../../../aysa_api/models/tree_navigation_item.types";
import type { Page } from "../../../../aysa_api/models/page.types";
import type { ProductSectionV2 } from "../../../../aysa_api/models/product_section_v2.types";

// The font of the text
export enum PageItem_TextContentBlock_Size {
  UNSPECIFIED = 0,
  SMALL = 1,
  MEDIUM = 2,
  LARGE = 3,
}

// The alignment of the text
export enum PageItem_TextContentBlock_Alignment {
  UNSPECIFIED = 0,
  LEFT = 1,
  CENTER = 2,
  RIGHT = 3,
}

// Layout defines the order of the text and media columns. Defaults to text-image.
export enum PageItem_ImageTextContentBlock_Layout {
  UNSPECIFIED = 0,
  IMAGE_TEXT = 1,
  TEXT_IMAGE = 2,
}

// Style defaults to primary when unset.
export enum PageItem_CtaButtonContentBlock_Style {
  UNSPECIFIED = 0,
  PRIMARY = 1,
  TEXT_LINK = 2,
}

export interface GetNavigationHeaderRequest {
  config: Config;
  storyId: number;
  targetGroup: TargetGroup;
  appliedFilters: AppliedFilters;
  // The category id the user is currently on
  categoryId?: number;
  reductionsState: ReductionsState;
  sortOptions: SortOptions;
}

export interface GetNavigationHeaderResponse {
  // The requested category which the user is currently on
  category: Category;
  // The list of categories which should be shown as the sub category navigation
  //
  // In case `category` is a leaf category, the categories will be the children of the parent category of `category`.
  // In all other cases, the `categories` will just be the children of the `category`.
  categories: GetNavigationHeaderResponse_CategoryItem[];
  // The direct parent category of `category`.
  //
  // This field will also not be set if the `category` is either a top level category (as these don't have parent)
  // but this will also not be set for second level categories (e.g. Clothes, Shoes etc.).
  parentCategory?: Category;
}

// Class which should be shown as the sub category navigation
export interface GetNavigationHeaderResponse_CategoryItem {
  category: Category;
  hasProducts: boolean;
}

export interface GetNavigationSectionCategoriesRequest {
  config: Config;
  storyId: number;
  targetGroup: TargetGroup;
  appliedFilters: AppliedFilters;
  // The category id the user is currently on
  categoryId?: number;
  reductionsState: ReductionsState;
  sortOptions: SortOptions;
}

export interface GetNavigationSectionCategoriesResponse {
  mainCategory: NavigationSection_Category;
}

export interface GetNavigationRequest {
  config: Config;
  storyId: number;
  targetGroup: TargetGroup;
  appliedFilters: AppliedFilters;
  // The category id the user is currently on
  categoryId?: number;
  reductionsState: ReductionsState;
  sortOptions: SortOptions;
}

export interface GetNavigationResponse {
  navigationSection: NavigationSection;
}

export interface GetHeaderRequest {
  config: Config;
  storyId: number;
}

export interface GetHeaderResponse {
  headline?: string;
  subline?: string;
  image: Image;
  brand?: GetHeaderResponse_Brand;
}

export interface GetHeaderResponse_Brand {
  id: number;
  logo?: Image;
  // in case we do not have a brand logo, we can use the brand name to display the brand name in the header
  brandName: string;
  promotionTracker: PromotionTracker;
}

export interface GetPageRequest {
  config: Config;
  session: AnySession;
  // The filters to be applied for the story page
  appliedFilters: AppliedFilters;
  limit: number;
  // The selected sort option or SORT_OPTIONS_UNSPECIFIED if default sorting is to be used
  sortOptions: SortOptions;
  // Pass the ids of Products which should be shown first in the response and get filtered out later in the stream.
  // Note: Should pass these ids for all pages of the stream and not just the first page to get a correct/non-duplicated products in the stream
  firstProductIds: number[];
  // The state of the reductions
  reductionsState: ReductionsState;
  preferredProductImageType?: ProductImageTypes;
  storyId: number;
  // The target group the customer is currently on, this is required to send the recommendedTargetGroup in the response back so FE can reroute the customer to the recommended target group if needed
  targetGroup?: TargetGroup;
  // The category the user is currently on, used to narrow down the product stream
  // In case no category is provided TDR defaults to the story's stream category
  categoryId?: number;
  // Whether or not to enable the AIUTA integration.
  // This might affect the badges that are shown on the products.
  enableAiuta: boolean;
}

export interface GetPageNextRequest {
  config: Config;
  session: AnySession;
  reductionsState: ReductionsState;
  preferredProductImageType?: ProductImageTypes;
  // The state of the story page
  continuationToken: string;
}

// GetPageResponse holds the ordered content of a story page.
//
// [items] carries the modular content blocks (Requirements 3.3 / 4.2.1) interleaved
// with the product stream, already sorted by the CMS-emitted position. A client that
// ignores [Item.position] can render in array order (Requirements 3.4 / 4.2).
export interface GetPageResponse {
  items: PageItem[];
  // The recommended target group for the customer, this is returned if the customer accessed the story page from a different target group than the one they are currently on
  recommendedTargetGroup?: TargetGroup;
  // The state of the story page
  continuationToken: string;
}

// GetPageNextResponse holds the next page of the story page.
export interface GetPageNextResponse {
  items: PageItem[];
  // The recommended target group for the customer, this is returned if the customer accessed the story page from a different target group than the one they are currently on
  recommendedTargetGroup?: TargetGroup;
  // The state of the story page
  continuationToken: string;
}

// PageItem is a single positioned entry in the story page: either a product section
// of the stream or one of the modular content blocks.
export interface PageItem {
  // The definitive CMS-emitted position of this entry, forwarded for tracking.
  // [items] is already sorted, so clients may also render in array order.
  position: number;
  type?:
    | {
        $case: "productSection";
        productSection: ProductSectionV2;
      }
    | {
        $case: "headline";
        headline: PageItem_HeadlineContentBlock;
      }
    | {
        $case: "text";
        text: PageItem_TextContentBlock;
      }
    | {
        $case: "imageTile";
        imageTile: PageItem_ImageTileContentBlock;
      }
    | {
        $case: "doubleTile";
        doubleTile: PageItem_DoubleTileContentBlock;
      }
    | {
        $case: "imageText";
        imageText: PageItem_ImageTextContentBlock;
      }
    | {
        $case: "productRow";
        productRow: PageItem_ProductRowContentBlock;
      }
    | {
        $case: "videoTile";
        videoTile: PageItem_VideoTileContentBlock;
      }
    | {
        $case: "outfit";
        outfit: PageItem_OutfitContentBlock;
      }
    | {
        $case: "ctaButton";
        ctaButton: PageItem_CtaButtonContentBlock;
      };
}

// Cta carries the optional call-to-action of a content block.
//
// GIVEN [show] = true  → [link_target] is present (CMS-enforced) and a CTA button is
// rendered; [text] is optional (FE renders the default "Discover more" when empty).
// GIVEN [show] = false AND [link_target] is present → the media alone is tappable /
// clickable and no button is rendered.
// See Requirements 3.3 / 5.2.
export interface PageItem_Cta {
  show: boolean;
  text?: string;
  linkTarget?: LinkTarget;
}

// StackVideos holds the video variant of a block. Each Video
// carries its own fallback image in the configured ratio (aysa_api.models.Video.fallback_image).
export interface PageItem_StackVideos {
  video: Video;
}

// HeadlineContentBlock is a single headline line. Not rendered when [text] is empty.
export interface PageItem_HeadlineContentBlock {
  text: string;
}

// TextContentBlock is a body-text paragraph Not
export interface PageItem_TextContentBlock {
  // The text of the block
  text: string;
  // The size of the text
  size: PageItem_TextContentBlock_Size;
  // The color of the text
  color: string;
  // The alignment of the text
  alignment: PageItem_TextContentBlock_Alignment;
}

// ImageTileContentBlock is a single image with optional copy and CTA. [ratio] applies
// to all stacks. See Requirements 4.2.1 / 5.2.
export interface PageItem_ImageTileContentBlock {
  // 1:1 | 3:4 | 4:3
  ratio: AspectRatio;
  images: PageItem_ImageTileContentBlock_Images;
  headline?: string;
  subline?: string;
  cta: PageItem_Cta;
  promotionTracker: PromotionTracker;
}

export interface PageItem_ImageTileContentBlock_Images {
  desktopDefault: Image;
  mobileDefault: Image;
}

// DoubleTileContentBlock renders two media tiles side by side (desktop) or stacked
// (app / mWeb). [ratio] applies to both sides and all stacks. See Requirements 4.2.1 / 5.2.
export interface PageItem_DoubleTileContentBlock {
  ratio: AspectRatio;
  left: PageItem_DoubleTileContentBlock_Side;
  right: PageItem_DoubleTileContentBlock_Side;
}

// Side holds exactly one of image or video (CMS rule). When [video] is set it
// carries its fallback image (aysa_api.models.Video.fallback_image).
export interface PageItem_DoubleTileContentBlock_Side {
  headline?: string;
  subline?: string;
  cta: PageItem_Cta;
  promotionTracker: PromotionTracker;
  media?:
    | {
        $case: "image";
        image: Image;
      }
    | {
        $case: "video";
        video: Video;
      };
}

// ImageTextContentBlock combines a text column with a media column. See Requirements 4.2.1 / 5.2.
export interface PageItem_ImageTextContentBlock {
  layout: PageItem_ImageTextContentBlock_Layout;
  headline?: string;
  bodyText: string;
  cta: PageItem_Cta;
  media: PageItem_ImageTextContentBlock_Media;
  promotionTracker: PromotionTracker;
}

// Media holds an image and/or a video; at least one is set. When both are present
// FE renders the video as primary and the image as static fallback (Requirements 5.2).
export interface PageItem_ImageTextContentBlock_Media {
  // Mobile-default ratio image, rendered across all stacks when no video is present.
  image?: Image;
  videos?: PageItem_StackVideos;
}

// ProductRowContentBlock is a fixed grid of products. [products] length is row_count * 4
// (desktop: 1 row of 4; app / mWeb: 2 rows of 2). See Requirements 4.2.1 / 4.3.
export interface PageItem_ProductRowContentBlock {
  rowCount: number;
  products: ProductTileV2[];
  sectionTracker: SectionTracker;
}

// VideoTileContentBlock is a single auto-playing video tile. [ratio] applies to all
// stacks; each video carries its fallback image. See Requirements 4.2.1 / 5.2.
export interface PageItem_VideoTileContentBlock {
  ratio: AspectRatio;
  videos: PageItem_StackVideos;
  linkTarget?: LinkTarget;
  promotionTracker: PromotionTracker;
}

// OutfitContentBlock renders a resolved CMS bundle. V1 always emits exactly one outfit
// (clients render a single-item slider); the repeated field lets V2 emit more without a
// breaking change. See Requirements 4.2.1 / 4.15.
export interface PageItem_OutfitContentBlock {
  outfits: PageItem_OutfitContentBlock_Outfit[];
}

export interface PageItem_OutfitContentBlock_Outfit {
  bundleId: number;
  // Link target for the model image AND the CTA (payload "outfitPageUrl").
  outfitPageLink: LinkTarget;
  // Total number of products in the bundle, rendered in the CTA copy.
  productCount: number;
  // Bundle products, carrying the same tile fields as Product Row products.
  products: ProductTileV2[];
  promotionTracker: PromotionTracker;
  // Exactly one of image or video. When [video] is set it carries its fallback image.
  media?:
    | {
        $case: "image";
        image: Image;
      }
    | {
        $case: "video";
        video: Video;
      };
}

// CtaButtonContentBlock is a standalone call-to-action button (the only fully new block
// in this migration). [text] and [link_target] are always set. See Requirements 4.2.1 / 5.2.
export interface PageItem_CtaButtonContentBlock {
  text: string;
  linkTarget: LinkTarget;
  style: PageItem_CtaButtonContentBlock_Style;
  promotionTracker: PromotionTracker;
}

export interface GetFiltersRequest {
  config: Config;
  session?: Session;
  targetGroup: TargetGroup;
  reductionsState?: ReductionsState;
  // In case no category is provided TDR defaults to the story's stream category
  categoryId?: number;
  appliedFilters: AppliedFilters;
  storyId: number;
  // The selected sort option or null if default sorting is to be used
  sortOptions?: SortOptions;
}

export interface GetFiltersResponse {
  filters: FilterSection[];
  // The overall product count for the received applied filters
  // won't be provided when we don't want it displayed
  productCount?: number;
  // The actually applied filters
  // Applied filters in the response may differ from the request, e.g. due to dropped filters
  appliedFilters: AppliedFilters;
  // Amount of applied filter values
  // e.g. color = red and green & size = M => 3
  appliedFiltersCount: number;
  // SEO story name
  title: string;
}

export interface GetCategoryTreeNavigationRequest {
  config: Config;
  session?: Session;
  targetGroup: TargetGroup;
  reductionsState?: ReductionsState;
  // The category the user is currently on
  // In case no category is provided TDR defaults to the story's stream category
  categoryId?: number;
  appliedFilters: AppliedFilters;
  storyId: number;
  sortOptions: SortOptions;
}

export interface GetCategoryTreeNavigationResponse {
  categoryTree: TreeNavigationItem[];
}

export interface MetadataRequest {
  config: Config;
  storyId: number;
  // Full URL of the page including query parameters.
  url: string;
}

export interface MetadataResponse {
  meta: Page;
}
